import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { demoAdminNotifications } from "@/data/demoData";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

export interface AdminBroadcast {
  id: string | number;
  title: string;
  message: string;
  type: string;
  target_audience: "all" | "students" | "partners";
  status: "draft" | "sent";
  recipients: string;
  recipient_count: number;
  delivered_count: number;
  created_by?: string | number;
  created_by_name?: string;
  source_broadcast_id?: string | number | null;
  sent_at?: string | null;
  date?: string;
  created_at?: string;
}

export interface AdminBroadcastStats {
  total_sent: number;
  drafts: number;
  delivered: number;
  delivery_rate: number;
  [key: string]: unknown;
}

export interface CreateBroadcastInput {
  title: string;
  message: string;
  type?: string;
  target_audience: "all" | "students" | "partners";
  send_now?: boolean;
}

const DEMO_BROADCASTS: AdminBroadcast[] = demoAdminNotifications.map((n, index) => ({
  id: `demo-${index + 1}`,
  title: n.title,
  message: n.message,
  type: n.type,
  target_audience: "all" as const,
  status: n.status === "sent" ? ("sent" as const) : ("draft" as const),
  recipients: n.recipients,
  recipient_count: 0,
  delivered_count: n.status === "sent" ? 1 : 0,
  created_by: "demo-admin",
  created_by_name: "Demo Admin",
  sent_at: n.status === "sent" ? `${n.date}T09:00:00Z` : null,
  date: n.date,
}));

const EMPTY_STATS: AdminBroadcastStats = {
  total_sent: 0,
  drafts: 0,
  delivered: 0,
  delivery_rate: 0,
};

export const ADMIN_BROADCASTS_KEY = ["admin-broadcasts"] as const;

export function useAdminBroadcastStats() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: [...ADMIN_BROADCASTS_KEY, "stats"],
    queryFn: async (): Promise<AdminBroadcastStats> => {
      if (user?.isDemo) {
        const sent = DEMO_BROADCASTS.filter((b) => b.status === "sent").length;
        const drafts = DEMO_BROADCASTS.filter((b) => b.status === "draft").length;
        const delivered = DEMO_BROADCASTS.reduce((sum, b) => sum + b.delivered_count, 0);
        return {
          total_sent: sent,
          drafts,
          delivered,
          delivery_rate: delivered > 0 ? 100 : 0,
        };
      }
      try {
        const res = await apiClient.get("/notifications/admin/broadcasts/stats");
        return unwrapApiObject<AdminBroadcastStats>(res, EMPTY_STATS);
      } catch {
        return EMPTY_STATS;
      }
    },
  });
}

export function useAdminBroadcasts() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ADMIN_BROADCASTS_KEY,
    queryFn: async (): Promise<AdminBroadcast[]> => {
      if (user?.isDemo) {
        return DEMO_BROADCASTS;
      }
      const res = await apiClient.get("/notifications/admin/broadcasts");
      return unwrapApiArray<AdminBroadcast>(res);
    },
  });
}

function invalidateBroadcasts(
  queryClient: ReturnType<typeof useQueryClient>,
  isDemo: boolean | undefined,
) {
  if (isDemo) {
    return;
  }
  queryClient.invalidateQueries({ queryKey: ADMIN_BROADCASTS_KEY });
}

export function useCreateBroadcast() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateBroadcastInput) => {
      if (user?.isDemo) {
        return { ...input, id: `demo-${Date.now()}` } as AdminBroadcast;
      }
      const res = await apiClient.post("/notifications/admin/broadcasts", input);
      return res.data.data as AdminBroadcast;
    },
    onSuccess: (broadcast) => {
      invalidateBroadcasts(queryClient, user?.isDemo);
      toast.success(broadcast.status === "sent" ? "Broadcast dispatched" : "Draft saved", {
        description:
          broadcast.status === "sent"
            ? `Delivered to ${broadcast.recipient_count ?? 0} recipient(s).`
            : "Dispatch it whenever you are ready.",
      });
    },
    onError: (error: any) => {
      toast.error("Failed to create broadcast", {
        description: error?.response?.data?.message ?? error?.message,
      });
    },
  });
}

export function useSendBroadcast() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { id, status: "sent" } as AdminBroadcast;
      }
      const res = await apiClient.post(`/notifications/admin/broadcasts/${id}/send`);
      return res.data.data as AdminBroadcast;
    },
    onSuccess: (broadcast) => {
      invalidateBroadcasts(queryClient, user?.isDemo);
      toast.success("Broadcast dispatched", {
        description: `Delivered to ${broadcast?.delivered_count ?? 0} recipient(s).`,
      });
    },
    onError: (error: any) => {
      toast.error("Failed to dispatch broadcast", {
        description: error?.response?.data?.message ?? error?.message,
      });
    },
  });
}

export function useResendBroadcast() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { id, status: "sent" } as AdminBroadcast;
      }
      const res = await apiClient.post(`/notifications/admin/broadcasts/${id}/resend`);
      return res.data.data as AdminBroadcast;
    },
    onSuccess: (broadcast) => {
      invalidateBroadcasts(queryClient, user?.isDemo);
      toast.success("Broadcast resent", {
        description: `Delivered to ${broadcast?.delivered_count ?? 0} recipient(s).`,
      });
    },
    onError: (error: any) => {
      toast.error("Failed to resend broadcast", {
        description: error?.response?.data?.message ?? error?.message,
      });
    },
  });
}

export function useDeleteBroadcast() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      if (user?.isDemo) {
        return { id } as AdminBroadcast;
      }
      await apiClient.delete(`/notifications/admin/broadcasts/${id}`);
      return { id } as AdminBroadcast;
    },
    onSuccess: (_data, id) => {
      if (user?.isDemo) {
        queryClient.setQueryData<AdminBroadcast[]>(ADMIN_BROADCASTS_KEY, (old) =>
          (old ?? []).filter((b) => String(b.id) !== String(id)),
        );
      } else {
        invalidateBroadcasts(queryClient, user?.isDemo);
      }
      toast.success("Broadcast deleted");
    },
    onError: (error: any) => {
      toast.error("Failed to delete broadcast", {
        description: error?.response?.data?.message ?? error?.message,
      });
    },
  });
}